// Global animation configuration with consistent timing and easing values
export const animationConfig = {
  // Timing durations (in seconds)
  duration: {
    fast: 0.15,
    normal: 0.3,
    slow: 0.5,
    slower: 0.8,
    slowest: 1.2,
  },
  
  // Easing functions
  easing: {
    // Standard easing curves
    linear: [0, 0, 1, 1],
    easeIn: [0.42, 0, 1, 1],
    easeOut: [0, 0, 0.58, 1],
    easeInOut: [0.42, 0, 0.58, 1],
    
    // Material Design easing curves
    material: [0.4, 0, 0.2, 1],
    materialIn: [0.4, 0, 1, 1],
    materialOut: [0, 0, 0.2, 1],
    
    // Bounce and elastic effects
    bounce: [0.68, -0.55, 0.265, 1.55],
    elastic: [0.68, -0.6, 0.32, 1.6],
    
    // Custom easing curves
    smooth: [0.25, 0.46, 0.45, 0.94],
    sharp: [0.4, 0, 0.6, 1],
    gentle: [0.6, 0.04, 0.98, 0.335],
  },
  
  // Common animation variants
  variants: {
    // Fade animations
    fadeIn: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    },
    
    fadeInUp: {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: 20 },
    },
    
    fadeInDown: {
      initial: { opacity: 0, y: -20 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -20 },
    },
    
    fadeInLeft: {
      initial: { opacity: 0, x: -20 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: -20 },
    },
    
    fadeInRight: {
      initial: { opacity: 0, x: 20 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: 20 },
    },
    
    // Scale animations
    scaleIn: {
      initial: { scale: 0.8, opacity: 0 },
      animate: { scale: 1, opacity: 1 },
      exit: { scale: 0.8, opacity: 0 },
    },
    
    scaleUp: {
      initial: { scale: 0.95, opacity: 0 },
      animate: { scale: 1, opacity: 1 },
      exit: { scale: 0.95, opacity: 0 },
    },
    
    // Slide animations
    slideUp: {
      initial: { y: '100%' },
      animate: { y: 0 },
      exit: { y: '100%' },
    },
    
    slideDown: {
      initial: { y: '-100%' },
      animate: { y: 0 },
      exit: { y: '-100%' },
    },
    
    slideLeft: {
      initial: { x: '100%' },
      animate: { x: 0 },
      exit: { x: '100%' },
    },
    
    slideRight: {
      initial: { x: '-100%' },
      animate: { x: 0 },
      exit: { x: '-100%' },
    },
    
    // Stagger animations for lists
    staggerContainer: {
      animate: {
        transition: {
          staggerChildren: 0.1,
        },
      },
    },
    
    staggerItem: {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
    },
  },
  
  // Common transition configurations
  transitions: {
    // Default transition
    default: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    },
    
    // Fast transition
    fast: {
      duration: 0.15,
      ease: [0.4, 0, 0.2, 1],
    },
    
    // Slow transition
    slow: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
    
    // Bounce transition
    bounce: {
      duration: 0.6,
      ease: [0.68, -0.55, 0.265, 1.55],
    },
    
    // Elastic transition
    elastic: {
      duration: 0.8,
      ease: [0.68, -0.6, 0.32, 1.6],
    },
  },
  
  // Hover and tap animations
  interactions: {
    hover: {
      scale: 1.02,
      transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
    },
    
    tap: {
      scale: 0.98,
      transition: { duration: 0.1, ease: [0.4, 0, 0.2, 1] },
    },
    
    focus: {
      scale: 1.01,
      transition: { duration: 0.15, ease: [0.4, 0, 0.2, 1] },
    },
  },
};

// Helper function to create custom animations with consistent timing
export const createAnimation = (variant, duration = 'normal', easing = 'material') => {
  return {
    ...animationConfig.variants[variant],
    transition: {
      duration: animationConfig.duration[duration],
      ease: animationConfig.easing[easing],
    },
  };
};

// Helper function to create staggered animations
export const createStaggeredAnimation = (delay = 0.1, staggerDelay = 0.1) => {
  return {
    container: {
      animate: {
        transition: {
          staggerChildren: staggerDelay,
          delayChildren: delay,
        },
      },
    },
    item: {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      transition: {
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1],
      },
    },
  };
};

// Export individual values for direct use
export const { duration, easing, variants, transitions, interactions } = animationConfig;
